import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getGuide, guides } from "@/content/guides";
import { Navbar1 } from "@/components/ui/navbar-1";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/guias/$slug")({
  loader: ({ params }) => {
    const guide = getGuide(params.slug);
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ params, loaderData }) => {
    const g = loaderData?.guide;
    const url = `https://viny.club/guias/${params.slug}`;
    if (!g) {
      return {
        meta: [{ title: "Guia — Viny Club" }],
        links: [{ rel: "canonical", href: url }],
      };
    }
    return {
      meta: [
        { title: `${g.title} | Viny Club` },
        { name: "description", content: g.description },
        { property: "og:title", content: g.title },
        { property: "og:description", content: g.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: g.title,
            description: g.description,
            datePublished: g.publishedAt,
            dateModified: g.updatedAt,
            author: { "@type": "Organization", name: "Viny Club" },
            publisher: {
              "@type": "Organization",
              name: "Viny Club",
              url: "https://viny.club",
            },
            mainEntityOfPage: url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Início", item: "https://viny.club/" },
              { "@type": "ListItem", position: 2, name: "Guias", item: "https://viny.club/guias" },
              { "@type": "ListItem", position: 3, name: g.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: GuideDetail,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center px-4 text-center">
      <div>
        <h1 className="text-2xl font-medium">Guia não encontrado</h1>
        <Link to="/guias" className="mt-4 inline-block text-sm underline">
          Ver todos os guias
        </Link>
      </div>
    </div>
  ),
});

function GuideDetail() {
  const { guide } = Route.useLoaderData();
  const related = guides.filter((g) => g.slug !== guide.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 />
      <main className="mx-auto max-w-2xl px-6 pt-28 pb-16">
        <nav aria-label="Breadcrumb" className="mb-8 text-xs text-muted-foreground">
          <Link to="/" className="hover:underline">
            Início
          </Link>{" "}
          ·{" "}
          <Link to="/guias" className="hover:underline">
            Guias
          </Link>
        </nav>

        <article>
          <header className="mb-10">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {guide.readingTime} de leitura
            </p>
            <h1 className="mt-3 text-4xl font-medium leading-tight tracking-tight">
              {guide.title}
            </h1>
            <p className="mt-4 text-base text-muted-foreground">{guide.description}</p>
          </header>

          <div className="text-[17px] leading-[1.75] text-foreground/90 [&_p]:mt-6 [&_p:first-child]:mt-0 [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-medium [&_h2]:tracking-tight [&_h2]:text-foreground [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-medium [&_ul]:mt-6 [&_ul]:space-y-2 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:mt-6 [&_ol]:space-y-2 [&_ol]:pl-6 [&_ol]:list-decimal [&_li]:pl-1 [&_li]:marker:text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:opacity-70 [&_strong]:font-medium [&_blockquote]:mt-6 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-5 [&_blockquote]:italic [&_blockquote]:text-muted-foreground">
            {guide.body}
          </div>
        </article>

        {related.length ? (
          <aside className="mt-16 border-t border-border pt-10">
            <h2 className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Continue lendo
            </h2>
            <ul className="space-y-4">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to="/guias/$slug"
                    params={{ slug: r.slug }}
                    className="block hover:opacity-70"
                  >
                    <div className="text-base font-medium">{r.title}</div>
                    <div className="text-sm text-muted-foreground">{r.description}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </main>
      <SiteFooter />
    </div>
  );
}
